import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  FaArrowRight,
  FaChartBar,
  FaCoins,
  FaReceipt,
  FaSearch,
  FaWarehouse,
  FaWhatsapp,
} from "react-icons/fa";
import { getStoredUser, isImpersonating } from "../utils/impersonation";
import { isPrimaryLinkedRestaurantSession } from "../utils/accountLinks";
import { canAccessFeature } from "../utils/rbac";
import { canAccessRoleUiRoute, ROLE_UI_PERMISSIONS_EVENT } from "../utils/roleUiPermissions";
import { APP_ESCAPE_EVENT, APP_SHORTCUT_EVENT, focusAndSelect } from "../utils/shortcuts";

const REPORTS = [
  {
    title: "Restaurants Overview",
    subtitle: "Cross-restaurant analytics for master access",
    route: "/reports/restaurants-overview",
    group: "Master Analytics",
    tone: "insights",
    masterOnly: true,
  },
  {
    title: "Sales Report",
    subtitle: "Summary by date range",
    route: "/sales-report",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Transaction Summary Report",
    subtitle: "Payments, taxes, discounts and order-channel summary",
    route: "/reports/transaction-summary",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Day-wise Item Report",
    subtitle: "Items sold on a specific date",
    route: "/reports/day-wise-items",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Day-wise Category Report",
    subtitle: "Category totals by day or month",
    route: "/reports/day-wise-category",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Month-wise Item Report",
    subtitle: "Items sold in a month",
    route: "/reports/month-wise-items",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Payment Method Report",
    subtitle: "Split by cash, card, UPI, credit",
    route: "/reports/payment-method",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Tax Report (GST)",
    subtitle: "GST rate breakdown & order tax summary",
    route: "/reports/tax-report",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Credit Payment Report",
    subtitle: "All credit orders for a date",
    route: "/reports/credit-payment",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Discounted Orders Report",
    subtitle: "Orders with an applied discount above 0%",
    route: "/reports/discounted-orders",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Split Payment Report",
    subtitle: "Split orders by payment mode",
    route: "/reports/split-payment",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Day-wise KOT Report",
    subtitle: "KOT activity by date range",
    route: "/reports/day-wise-kot",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Custom Excel Report",
    subtitle: "Generate order, date, or item-wise Excel exports",
    route: "/reports/custom-excel",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Order Edit History",
    subtitle: "Audit bill and order changes by staff",
    route: "/order-edit-history",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Deleted Orders",
    subtitle: "View and restore deleted bills & orders",
    route: "/deleted-orders",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Finance Report",
    subtitle: "Expense vs income",
    route: "/finance/report",
    group: "Finance",
    tone: "finance",
  },
  {
    title: "Profit & Loss Report",
    subtitle: "Sales and income vs expenses with net profit",
    route: "/finance/profit-loss",
    group: "Finance",
    tone: "finance",
  },
  {
    title: "Day Book Entry Report",
    subtitle: "Day-wise sales, income and expense entries",
    route: "/finance/day-book",
    group: "Finance",
    tone: "finance",
  },
  {
    title: "Top Products",
    subtitle: "Best selling items",
    route: "/reports/top-products",
    group: "Performance Insights",
    tone: "insights",
  },
  {
    title: "Top Waiters",
    subtitle: "Best performing staff",
    route: "/reports/top-waiters",
    group: "Performance Insights",
    tone: "insights",
  },
  {
    title: "Hourly Sales",
    subtitle: "Heatmap by hour",
    route: "/reports/hourly-sales",
    group: "Performance Insights",
    tone: "insights",
  },
  {
    title: "Category Performance",
    subtitle: "Category contribution",
    route: "/reports/category-performance",
    group: "Performance Insights",
    tone: "insights",
  },
  {
    title: "Table Utilization",
    subtitle: "Occupancy and turns",
    route: "/reports/table-utilization",
    group: "Performance Insights",
    tone: "insights",
  },
  {
    title: "Table-wise Sales List",
    subtitle: "Bills grouped by table",
    route: "/reports/table-wise-sales-list",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Table-wise Product Sales",
    subtitle: "Product count by selected tables",
    route: "/reports/table-wise-product-sales",
    group: "Sales & Revenue",
    tone: "sales",
  },
  {
    title: "Stock Summary Report",
    subtitle: "Opening, in, used and closing stock snapshot",
    route: "/reports/inventory/stock-summary",
    group: "Inventory",
    tone: "inventory",
  },
  {
    title: "Daily Usage Report",
    subtitle: "Item-wise consumption for a selected date",
    route: "/reports/inventory/daily-usage",
    group: "Inventory",
    tone: "inventory",
  },
  {
    title: "Low Stock Report",
    subtitle: "Items below minimum stock with reorder quantity",
    route: "/reports/inventory/low-stock",
    group: "Inventory",
    tone: "inventory",
  },
  {
    title: "Item Performance Report",
    subtitle: "Date-range usage and value by item",
    route: "/reports/inventory/item-performance",
    group: "Inventory",
    tone: "inventory",
  },
];

const ReportsPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const supportWhatsapp = "919988777409";
  const viewer = getStoredUser();
  const [, setPermissionVersion] = useState(0);
  useEffect(() => {
    const refresh = () => setPermissionVersion((version) => version + 1);
    window.addEventListener(ROLE_UI_PERMISSIONS_EVENT, refresh);
    return () => window.removeEventListener(ROLE_UI_PERMISSIONS_EVENT, refresh);
  }, []);
  const isMasterReportViewer =
    canAccessFeature("impersonation", viewer) || isImpersonating();
  const isPrimaryLinkedViewer = isPrimaryLinkedRestaurantSession();
  const canViewRestaurantsOverview =
    isMasterReportViewer || isPrimaryLinkedViewer;

  const visibleReports = useMemo(
    () =>
      REPORTS.filter((item) => {
        if (item.route === "/reports/restaurants-overview") {
          return canViewRestaurantsOverview;
        }

        if (isMasterReportViewer && !isPrimaryLinkedViewer) {
          return false;
        }

        return item.masterOnly !== true && canAccessRoleUiRoute(item.route, viewer);
      }),
    [canViewRestaurantsOverview, isMasterReportViewer, isPrimaryLinkedViewer, viewer]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleReports;
    return visibleReports.filter((item) =>
      `${item.title} ${item.subtitle} ${item.group}`.toLowerCase().includes(q)
    );
  }, [query, visibleReports]);

  const grouped = useMemo(
    () =>
      filtered.reduce((acc, item) => {
        if (!acc[item.group]) acc[item.group] = [];
        acc[item.group].push(item);
        return acc;
      }, {}),
    [filtered]
  );

  const groups = Object.entries(grouped);

  const whatsappUrl = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data || {};
    const restaurant = user?.restaurant_name || user?.name || "my restaurant";
    const msg =
      `Hi Team, I need a custom report for ${restaurant}. ` +
      "Please help me build a custom report as per my requirement.";
    return `https://wa.me/${supportWhatsapp}?text=${encodeURIComponent(msg)}`;
  }, []);

  useEffect(() => {
    const onShortcut = (event) => {
      switch (event?.detail?.id) {
        case "reports.search":
          if (focusAndSelect(searchInputRef.current)) event.preventDefault();
          return;
        case "reports.open-first-result":
          if (
            !filtered.length ||
            (!query.trim() && document.activeElement !== searchInputRef.current)
          ) {
            return;
          }
          event.preventDefault();
          navigate(filtered[0].route);
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [filtered, navigate, query]);

  useEffect(() => {
    const onEscape = (event) => {
      const isFocused = document.activeElement === searchInputRef.current;
      if (!isFocused && !query.trim()) return;
      event.preventDefault();
      searchInputRef.current?.blur?.();
      if (query.trim()) setQuery("");
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [query]);

  return (
    <Page>
      <Hero>
        <HeroTop>
          <div>
            <Badge>Reports Hub</Badge>
            <Title>Business Intelligence Center</Title>
            <Sub>All reports in one place with quick access and cleaner navigation.</Sub>
          </div>
          <Stats>
            <StatCard>
              <b>{visibleReports.length}</b>
              <span>Total Reports</span>
            </StatCard>
            <StatCard>
              <b>{Object.keys(grouped).length}</b>
              <span>Active Categories</span>
            </StatCard>
          </Stats>
        </HeroTop>
        <SearchWrap>
          <FaSearch />
          <input
            ref={searchInputRef}
            data-shortcut-search="page"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search report by name, category or purpose..."
          />
        </SearchWrap>
      </Hero>

      {groups.length === 0 ? (
        <EmptyState>No reports found for this search.</EmptyState>
      ) : (
        groups.map(([groupName, items]) => (
          <Section key={groupName}>
            <SectionHead>
              <SectionIcon>
                {groupName === "Sales & Revenue" ? (
                  <FaReceipt />
                ) : groupName === "Finance" ? (
                  <FaCoins />
                ) : groupName === "Performance Insights" ? (
                  <FaChartBar />
                ) : (
                  <FaWarehouse />
                )}
              </SectionIcon>
              <div>
                <SectionTitle>{groupName}</SectionTitle>
                <SectionSub>{items.length} reports</SectionSub>
              </div>
            </SectionHead>

            <Grid>
              {items.map((item) => (
                <Card key={item.route} to={item.route}>
                  <CardTop>
                    <Tag $tone={item.tone}>{item.group}</Tag>
                    <Dot $tone={item.tone} />
                  </CardTop>
                  <CardTitle>{item.title}</CardTitle>
                  <CardSub>{item.subtitle}</CardSub>
                  <Open>
                    Open Report <FaArrowRight size={12} />
                  </Open>
                </Card>
              ))}
            </Grid>
          </Section>
        ))
      )}

      <CustomReportCta>
        <div>
          <h4>Need a Custom Report?</h4>
          <p>Tell us your requirement on WhatsApp and we will help you build it.</p>
        </div>
        <WhatsAppBtn
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
        >
          <FaWhatsapp size={16} />
          Request Custom Report
        </WhatsAppBtn>
      </CustomReportCta>
    </Page>
  );
};

export default ReportsPage;

const Page = styled.div`
  min-height: 100%;
  padding: clamp(10px, 2vw, 18px);
  background: var(--app-bg);
  display: grid;
  gap: 16px;
  align-content: start;
  grid-auto-rows: max-content;
`;

const Hero = styled.div`
  background: linear-gradient(125deg, var(--app-primary-dark), var(--app-primary-dark) 48%, var(--app-primary));
  color: #fff;
  border-radius: 20px;
  padding: clamp(14px, 2vw, 20px);
  box-shadow: 0 16px 34px rgba(var(--app-primary-rgb), 0.2);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const HeroTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 12px;
`;

const Title = styled.h1`
  margin: 8px 0 4px;
  font-size: clamp(1.3rem, 2.8vw, 1.9rem);
  font-weight: 900;
`;

const Sub = styled.div`
  opacity: 0.9;
  max-width: 700px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(120px, 1fr));
  gap: 10px;
  width: min(300px, 100%);
`;

const StatCard = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.1);
  padding: 10px;
  display: grid;
  gap: 2px;
  b {
    font-size: 1.2rem;
  }
  span {
    font-size: 12px;
    opacity: 0.9;
  }
`;

const SearchWrap = styled.label`
  margin-top: 14px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  svg {
    opacity: 0.85;
  }
  input {
    border: 0;
    outline: none;
    background: transparent;
    color: #fff;
    width: 100%;
    font-size: 14px;
  }
  input::placeholder {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const Section = styled.section`
  display: grid;
  gap: 10px;
`;

const SectionHead = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SectionIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--app-primary);
  color: #fff;
  display: grid;
  place-items: center;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: var(--app-text);
`;

const SectionSub = styled.div`
  font-size: 12px;
  color: var(--app-text-muted);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`;

const Card = styled(Link)`
  background: var(--app-surface);
  border: 1px solid #dce8f6;
  border-radius: 16px;
  padding: 14px;
  text-decoration: none;
  color: var(--app-text);
  box-shadow: 0 10px 24px rgba(10, 40, 75, 0.08);
  display: grid;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 34px rgba(2, 132, 199, 0.22);
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  padding: 4px 8px;
  border-radius: 999px;
  width: fit-content;
  color: ${({ $tone }) =>
    $tone === "inventory"
      ? "#0f766e"
      : $tone === "finance"
      ? "#7c3aed"
      : $tone === "insights"
      ? "#9a3412"
      : "var(--app-primary)"};
  background: ${({ $tone }) =>
    $tone === "inventory"
      ? "#ccfbf1"
      : $tone === "finance"
      ? "#ede9fe"
      : $tone === "insights"
      ? "#ffedd5"
      : "var(--app-primary-soft)"};
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "inventory"
      ? "#14b8a6"
      : $tone === "finance"
      ? "#8b5cf6"
      : $tone === "insights"
      ? "#f97316"
      : "var(--app-primary-hover)"};
`;

const CardTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 800;
`;

const CardSub = styled.div`
  color: var(--app-text-muted);
  font-size: 0.9rem;
`;

const Open = styled.div`
  margin-top: 6px;
  font-weight: 800;
  color: var(--app-primary);
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const EmptyState = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  padding: 22px;
  text-align: center;
  color: var(--app-text-muted);
  background: var(--app-surface);
`;

const CustomReportCta = styled.div`
  border-radius: 16px;
  border: 1px solid #cde8d7;
  background: linear-gradient(135deg, #effdf4 0%, #e7fbff 100%);
  box-shadow: 0 10px 20px rgba(2, 132, 199, 0.08);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  h4 {
    margin: 0;
    color: var(--app-text);
    font-size: 1rem;
  }

  p {
    margin: 4px 0 0;
    color: var(--app-text-muted);
    font-size: 0.9rem;
  }
`;

const WhatsAppBtn = styled.a`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  border: 1px solid #0f9f5f;
  background: linear-gradient(135deg, #25d366, #1faa56);
  color: #fff;
  font-weight: 800;
  padding: 10px 14px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(37, 211, 102, 0.3);
  }
`;
